import type { PriceBreakTier } from '@/lib/api-types'

/**
 * Pack-option helpers for the purchase-request wizard. Each PriceBreakTier
 * IS a pack option: packSizeKg is the kg content of one pack, pricePerKg is
 * the per-kg price when buying that pack. Everything shown in the wizard is
 * advisory — the server re-prices every line from its validated priceBreakId
 * on submit, so the client never sends pricing.
 */
export interface PackOption {
  id: string
  label: string
  packSizeKg: number
  pricePerKg: number
}

function toOption(tier: PriceBreakTier): PackOption {
  return {
    id: tier.id,
    label: tier.uomName ?? `${tier.packSizeKg}kg`,
    packSizeKg: tier.packSizeKg,
    pricePerKg: tier.pricePerKg,
  }
}

/** A product's pack options, verbatim from its price breaks (server order: packSizeKg ASC). */
export function packOptions(tiers: PriceBreakTier[]): PackOption[] {
  return tiers.map(toOption)
}

/** Packs × kg per pack × per-kg price for that pack. */
export function lineTotal(option: PackOption, quantity: number): number {
  return quantity * option.packSizeKg * option.pricePerKg
}

/**
 * The cheapest pack with a strictly lower per-kg price than the selected one
 * — the "Save $X.XX/kg with the 180kg Drum" nudge — or null when the
 * selection is already the cheapest (or unknown).
 */
export function cheaperOption(tiers: PriceBreakTier[], selectedId: string): PackOption | null {
  const selected = tiers.find((tier) => tier.id === selectedId)
  if (!selected) return null
  let cheapest: PriceBreakTier | null = null
  for (const tier of tiers) {
    if (
      tier.pricePerKg < selected.pricePerKg &&
      (!cheapest || tier.pricePerKg < cheapest.pricePerKg)
    ) {
      cheapest = tier
    }
  }
  return cheapest ? toOption(cheapest) : null
}
